import { JSX } from 'react';
import {
    Edit,
    SimpleForm,
    SelectInput,
    ReferenceInput,
    TextInput,
    required,
    BooleanInput,
    NumberInput,
    ReferenceManyField,
    Datagrid,
    TextField,
    NumberField,
    SelectField,
    EditButton,
    BooleanField,
    TabbedForm,
    Link,
    WithRecord,
    useRefresh,
    useRecordContext,
    useUpdate,
    useNotify,
    SimpleFormIterator,
    ArrayInput,
} from 'react-admin';
import {
    LinearProgress,
    Grid,
    Box
} from '@mui/material';
import { SubscriptionProduct } from "../../types/entities";
import { useGetSubscriptionTiers, useStripeModeFetch } from "../global-hooks/default-system-hooks";
import { SubscriptionProductNewPriceModal } from "./components/SubscriptionProduct/SubscriptionProductNewPriceModal";
import { ChatFieldBridge } from "../chat-bot/components/ChatFieldBridge";

const FormBlock: () => JSX.Element = ():JSX.Element => {
    const record: SubscriptionProduct = useRecordContext();
    const { loadedSubscriptionTiers, loadingSubscriptionTiers } = useGetSubscriptionTiers();
    const { isTestMode, loadingTestModeData } = useStripeModeFetch();
    const refresh: any = useRefresh();
    const notify: any = useNotify();
    const [update] = useUpdate();
    // TODO - Add button on this page to copy the product to livemode. Will still only use the version from the setting though

    const currency: { id: string, name: string }[] = [
        { id: 'gbp', name: 'GBP' },
    ];
    function handleSave(values: any): void {
        update(
            'subscription_products',
            { id: record.id, data: values, previousData: record },
            {
                onSuccess: async (data: any): Promise<void> => {
                    notify("Product successfully updated!", { type: "success" });
                    refresh();

                },
                onError: (error: any): void => {
                    notify(`Failed to update error: ${error.message}`, { type: 'error' });
                },
            }
        );
    }

    return (
        <TabbedForm onSubmit={ handleSave } >
            <TabbedForm.Tab label="General">
                <TextInput source="name" validate={ [required()] } />
                <BooleanInput source="active"
                              helperText="Enable/Disable will also update Stripe"
                              validate={ [required()] } />
                <BooleanInput source="hideFromCustomer"
                              helperText="Enabling will hide the Tier and pricing from the customer and disable purchase"
                              validate={ [required()] } defaultValue={ false } />
                <Box sx={ { width: '100%', margin: '10px 0', display: 'flex', justifyContent: 'space-between' } } >
                    <span>{ "Is product used in production:" }</span>
                    <BooleanField source="liveMode" />
                </Box>
                { (loadingSubscriptionTiers) &&
                    <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                }
                { (!loadingSubscriptionTiers) &&
                    <SelectInput
                        name="tierLevel"
                        source="tierLevel"
                        optionValue="id"
                        choices={ loadedSubscriptionTiers }
                        validate={ [required()] }
                    />
                }
                <NumberInput source="monthlyCredits" />
                <NumberInput source="annualCredits" />
                {/* Inside a tab, not beside them: TabbedFormTabs clones every direct child into the
                    tab bar. One tab is enough - react-admin mounts them all and hides the
                    inactive ones, so the whole form is registered from here. */}
                <ChatFieldBridge />
            </TabbedForm.Tab>
            <TabbedForm.Tab label="CMS">
                <TextInput source="strapLine"  />
                <TextInput source="customerPortalPromoPill" />
                <BooleanInput source="landingPagePriceBlockFeatured"
                              helperText="Will render as a white taller block on landing pages, if more than one is enabled, the one shown, could be random"
                              name="landingPagePriceBlockFeatured" />
                <ArrayInput source="landingPagePriceBlockPoints" name="landingPagePriceBlockPoints" >
                    <SimpleFormIterator>
                        <TextInput source="item" />
                    </SimpleFormIterator>
                </ArrayInput>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Pricing">
                <Box sx={ {
                    width: '100%',
                    margin: '0 0 20px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                } }>
                    <Box sx={ { width: '75%%', display: 'flex', flexDirection: 'column', margin: '0 0 30px 0' } } >
                        <span style={ { fontSize: '1.2rem', margin: '0 0 10px 0' } } >{ "Product Pricing. Each product can have an active monthly and annual price." }</span>
                        <span style={ { fontSize: '1rem' } } >
                            { "More than one of each price can be added but this might not guarantee the correct price being selected. Deactivate unused prices (this will update Stripe)" }
                        </span>
                        <span style={ { fontSize: '1rem' } } >
                            { "Default Price is a required Stripe setting. Only active prices for each frequency will be used during customer checkout. Custom prices are unique to a subscription, be careful when disabling." }
                        </span>
                    </Box>
                    { (record !== undefined) &&
                        <SubscriptionProductNewPriceModal record={ record } />
                    }

                </Box>
                <ReferenceManyField reference="subscription_product_prices"
                                    source="subscriptionProductPrices"
                                    target="id" >
                    <Datagrid bulkActionButtons={ false }
                              sx={ {
                                  width: '100%'
                              } }
                    >
                        <TextField source="id" />
                        <TextField source="name" />
                        <BooleanField source="active" />
                        <BooleanField source="defaultPrice" />
                        <NumberField source="price" label="Price (£)" />
                        <SelectField source="currency" choices={ currency } />
                        <TextField source="priceFrequency" sx={ { textTransform: 'capitalize' } } />
                        <EditButton />
                    </Datagrid>
                </ReferenceManyField>
            </TabbedForm.Tab>
            <TabbedForm.Tab label="Stripe Data">
                <WithRecord render={
                    (record: SubscriptionProduct): JSX.Element => {
                        return (
                            <Box sx={ { width: '100%', margin: '10px 0', display: 'flex', justifyContent: 'space-between' } } >
                                <span>{ "Stripe Product ID:" }</span>
                                { (loadingTestModeData) &&
                                    <LinearProgress sx={ { margin: '10px 0', width: '75%' } } />
                                }
                                { (!loadingTestModeData) &&
                                    <span>
                                            { (isTestMode) &&
                                                <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/test/products/" + record.stripeId }
                                                      target="_blank"
                                                >{ record.stripeId }</Link>
                                            }
                                        {(!isTestMode) &&
                                            <Link to={ "https://dashboard.stripe.com/acct_1S676LIYyPSARt7l/products/" + record.stripeId }
                                                  target="_blank"
                                            >{ record.stripeId }</Link>
                                        }
                                    </span>
                                }
                            </Box>
                        );
                    }
                } />
            </TabbedForm.Tab>
        </TabbedForm>
    );
}

export const SubscriptionProductEdit: () => JSX.Element = (): JSX.Element => {
    return (
        <Edit redirect={ false }>
            <FormBlock />
        </Edit>
    );
}
